import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { extractMemberFromBettie } from './utils/extractMemberFromBettie';
import { attachJosa } from '../../utils/bettieUtils';
import { useBettieSearch } from '../../domains/bettie/ui/hooks/useBettieSearch';
import { useBettieProfileImageOverlay } from '../chat/hooks/useBettieProfileImageOverlay';
import { usePersonaManager } from './hooks/usePersonaManager';
import { useSceneStepFlow } from './hooks/useSceneStepFlow';
import AddSceneArtistsBox from './components/AddSceneArtistsBox';
import LoadingSceneStep from './components/LoadingSceneStep';
import SceneBridgeStep from './components/SceneBridgeStep';
import PersonaInputStep from './components/PersonaInputStep';
import EnterArtistInfoStep from './components/EnterArtistInfoStep';
import EnterUserInfoStep from './components/EnterUserInfoStep';
import AnimationStarBeam from './components/AnimationStarBeam';
import BettieProfileImageOverlay from '../chat/components/BettieProfileImageOverlay';

const SceneSettingPage = () => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();

  const flow = useSceneStepFlow();

  const persona = usePersonaManager({
    closeBottomSheet: flow.closeBottomSheet
  });

  const cancelPersonaFlow = () => {
    persona.actions.cancelPersonaFlow(
      persona.state.activePersonaSlot === 'second' ? 'secondPersonaInput' : 'firstPersonaSelected'
    );
    flow.closeBottomSheet();
  };

  const [firstNickname, setFirstNickname] = useState('');
  const [secondNickname, setSecondNickname] = useState('');
  const firstBettieSearch = useBettieSearch(firstNickname.trim(), { enabled: false });
  const secondBettieSearch = useBettieSearch(secondNickname.trim(), { enabled: false });

  const firstPhotoOverlay = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl }) => persona.actions.setPersonaDataUrl('first', dataUrl)
  });
  const secondPhotoOverlay = useBettieProfileImageOverlay({
    onConfirm: ({ dataUrl }) => persona.actions.setPersonaDataUrl('second', dataUrl)
  });

  const handleResetCombination = () => {
    persona.actions.resetPersonas();
    setFirstNickname('');
    setSecondNickname('');
  };

  const handleBridgeComplete = () => persona.actions.setStep('firstPersonaSelected');

  const handleFirstNext = async () => {
    const trimmed = firstNickname.trim();
    if (!trimmed) return;
    const { data } = await firstBettieSearch.refetch();
    const bettieData = data?.bettie;
    const memberInfo = extractMemberFromBettie(bettieData);
    if (bettieData && memberInfo) {
      persona.actions.handlePersonaFromSearchBySlot(
        {
          kind: 'artist',
          persona: {
            nickname: trimmed,
            dataUrl: '',
            memberPersonaId: (bettieData as { memberPersonaId?: string }).memberPersonaId,
            memberId: memberInfo.memberId,
            memberName: memberInfo.memberName
          }
        },
        'first'
      );
    } else {
      persona.actions.handlePersonaFromSearchBySlot(
        { kind: 'celeb', persona: { nickname: trimmed, dataUrl: '' } },
        'first'
      );
    }
  };

  const handleSecondNext = async () => {
    const trimmed = secondNickname.trim();
    if (!trimmed) return;
    const { data } = await secondBettieSearch.refetch();
    const bettieData = data?.bettie;
    const memberInfo = extractMemberFromBettie(bettieData);
    if (bettieData && memberInfo) {
      persona.actions.handlePersonaFromSearchBySlot(
        {
          kind: 'artist',
          persona: {
            nickname: trimmed,
            dataUrl: '',
            memberPersonaId: (bettieData as { memberPersonaId?: string }).memberPersonaId,
            memberId: memberInfo.memberId,
            memberName: memberInfo.memberName
          }
        },
        'second'
      );
    } else {
      persona.actions.handlePersonaFromSearchBySlot(
        { kind: 'celeb', persona: { nickname: trimmed, dataUrl: '' } },
        'second'
      );
    }
  };

  if (persona.state.step === 'bridge') {
    return <SceneBridgeStep onComplete={handleBridgeComplete} />;
  }

  if (persona.state.step === 'firstPersonaSelected') {
    return (
      <PersonaInputStep
        slot="first"
        onBack={handleHistoryBack}
        onSetAsMe={() => persona.actions.handleSetSlotAsUser('first')}
        nickname={firstNickname}
        onNicknameChange={setFirstNickname}
        onNext={handleFirstNext}
      />
    );
  }

  if (persona.state.step === 'secondPersonaInput') {
    return (
      <PersonaInputStep
        slot="second"
        onBack={persona.actions.goBackFromSecond}
        onSetAsMe={() => persona.actions.handleSetSlotAsUser('second')}
        nickname={secondNickname}
        onNicknameChange={setSecondNickname}
        onNext={handleSecondNext}
        isFirstPersonaUser={persona.state.firstPersona?.entityType === 'User'}
        firstPersonaNickname={persona.state.firstPersona?.nickname ?? ''}
      />
    );
  }

  if (persona.state.step === 'loadingScene') {
    return (
      <LoadingSceneStep
        firstPersona={persona.state.firstPersona}
        secondPersona={persona.state.secondPersona}
      />
    );
  }

  if (persona.state.step === 'enterArtistInfo') {
    const p = persona.state.pendingPersona;
    return (
      <EnterArtistInfoStep
        nickname={p?.nickname ?? ''}
        initialOccupationType={p?.occupationType}
        initialOccupationDescription={p?.occupationDescription}
        onBack={cancelPersonaFlow}
        onSubmit={persona.actions.handleEnterArtistInfoSubmit}
      />
    );
  }

  if (persona.state.step === 'enterUserInfo') {
    const p = persona.state.pendingPersona;
    return (
      <EnterUserInfoStep
        initialNickname={p?.nickname ?? ''}
        initialDataUrl={p?.dataUrl}
        initialGender={p?.gender}
        initialAgeGapType={p?.ageGapType}
        initialPersonality={p?.personality}
        onBack={cancelPersonaFlow}
        onSubmit={persona.actions.handleEnterUserInfoSubmit}
      />
    );
  }

  return (
    <div
      className="min-h-screen relative pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      style={{
        backgroundImage: 'url(/assets/scene/bg_scene_setting.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <Helmet>
        <title>{t('SceneSetting.title')}</title>
      </Helmet>
      <header className="px-[16px] py-[15px] z-[1] relative">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>
      <div
        className="rounded-full w-[1020px] h-[1020px] absolute top-[-798px] left-1/2 -translate-x-1/2 flex flex-col justify-end items-center"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 50%, #0D0E12 23.08%, #0D0E12 73.08%, #130757 92.31%, #19009C 100%)'
        }}
      >
        <AnimationStarBeam />
        <div
          className="text-pure-white mb-[50px] flex flex-col items-center"
          style={{
            fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif'
          }}
        >
          <div className="flex items-center gap-[3px]">
            {attachJosa(
              persona.state.firstPersona?.nickname === '나'
                ? '내'
                : (persona.state.firstPersona?.nickname ?? ''),
              '이/가'
            )}{' '}
            먼저 움직였고,
          </div>
          <div className="flex items-center">
            {attachJosa(persona.state.secondPersona?.nickname ?? '', '은/는')} 거기에 맞춰 움직였다.
          </div>
        </div>
      </div>
      <main className="flex flex-col items-center mt-[180px]">
        <p className="text-[20px] font-semibold leading-[24px] text-pure-white">
          {t('SceneSetting.description')}
        </p>

        <AddSceneArtistsBox
          firstPersona={persona.state.firstPersona}
          secondPersona={persona.state.secondPersona}
          onAddSceneArtist={(slot) =>
            slot === 'first'
              ? firstPhotoOverlay.openImagePicker()
              : secondPhotoOverlay.openImagePicker()
          }
        />
      </main>
      <input {...firstPhotoOverlay.fileInputProps} />
      <input {...secondPhotoOverlay.fileInputProps} />
      <BettieProfileImageOverlay {...firstPhotoOverlay.overlayProps} />
      <BettieProfileImageOverlay {...secondPhotoOverlay.overlayProps} />
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+16px)] flex flex-col items-center gap-[20px]">
        <Button
          size="large"
          disabled={!persona.state.canProceedNext}
          className="w-full text-[17px] font-semibold disabled:bg-disabled-dark disabled:text-onDisabled-dark"
          onClick={persona.actions.handleNext}
        >
          {t('SceneSetting.startButton')}
        </Button>
        <button
          type="button"
          className="text-[15px] font-medium leading-[18px] text-pure-white"
          onClick={handleResetCombination}
        >
          {t('SceneSetting.resetButton')}
        </button>
      </div>
    </div>
  );
};

export default SceneSettingPage;
